import type { VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva } from 'class-variance-authority'
import { clsx } from 'clsx'

const pillVariants = cva(
  'ds-inline-flex ds-border-half ds-border-white-300 ds-items-center ds-justify-center ds-whitespace-nowrap ds-font-text ds-transition-colors ds-focus-visible:ds-outline-none ds-focus-visible:ds-ring-1 ds-focus-visible:ds-ring-offset-2',
  {
    variants: {
      variant: {
        default:
          'ds-bg-white-50 ds-text-black-900 hover:ds-bg-white-100 ds-font-medium',
        primary:
          'ds-bg-blue-100 ds-text-blue-900 hover:ds-bg-blue-200 ds-font-medium',
        secondary:
          'ds-bg-gray-100 ds-text-gray-900 hover:ds-bg-gray-200 ds-font-medium',
        destructive:
          'ds-bg-red-100 ds-text-red-900 hover:ds-bg-red-200 ds-font-medium',
        success:
          'ds-bg-green-100 ds-text-green-900 hover:ds-bg-green-200 ds-font-medium',
        warning:
          'ds-bg-yellow-100 ds-text-yellow-900 hover:ds-bg-yellow-200 ds-font-medium',
      },
      size: {
        sm: 'ds-text-12',
        md: 'ds-text-13',
        lg: 'ds-text-14',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
)

export interface PillProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof pillVariants> {
  asChild?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const Pill = React.forwardRef<HTMLDivElement, PillProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      leftIcon,
      rightIcon,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'div'

    const sizeStyles = {
      sm: { padding: '2px 4px' },
      md: { padding: '3px 6px' },
      lg: { padding: '4px 8px' },
    }

    return (
      <Comp
        className={clsx(
          pillVariants({ variant, size, className }),
          'ds-rounded-[6px]',
        )}
        ref={ref}
        style={size ? sizeStyles[size] : sizeStyles.md}
        {...props}
      >
        {leftIcon && <span style={{ marginRight: '4px' }}>{leftIcon}</span>}
        {children}
        {rightIcon && <span style={{ marginLeft: '4px' }}>{rightIcon}</span>}
      </Comp>
    )
  },
)

Pill.displayName = 'Pill'

export { Pill, pillVariants }
